import { useAtom } from 'jotai';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { GeneratingDots } from '@/components/glitch-ui/generating-dots';
import { landingPageByCampaignAtom } from '@/store';
import { campaignLandingPageQueryOptions, landingPageQueryOptions } from '@/lib/query-options';
import { createLandingPage } from '@/lib/apis';
import { toast } from '@/hooks/useToast';
import type { CampaignData } from '@/lib/types';
import { LandingPageView } from './landing-page-view';
import { LandingPageError } from './landing-page-error';
import { LandingPageGenerating } from './landing-page-generating';

export function LandingPageTab({ campaign }: { campaign: CampaignData }) {
  const queryClient = useQueryClient();
  const [pageMap, setPageMap] = useAtom(landingPageByCampaignAtom);
  // The atom survives in-session navigation; the by-campaign lookup survives hard reloads.
  const trackedId = pageMap[campaign.id] ?? null;
  const lookup = useQuery(campaignLandingPageQueryOptions(trackedId ? null : campaign.id, campaign.organization_id));
  const publicId = trackedId ?? lookup.data?.public_id ?? null;

  const { data: landingPage, isLoading, isError } = useQuery(landingPageQueryOptions(publicId, campaign.organization_id));

  const generate = useMutation({
    mutationFn: () => createLandingPage(campaign.id, campaign.organization_id),
    onSuccess: (page) => {
      setPageMap((prev) => ({ ...prev, [campaign.id]: page.public_id }));
      queryClient.setQueryData(['landing-page', page.public_id], page);
    },
    onError: (error) => {
      toast({
        title: 'Failed to generate landing page',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  if (!trackedId && lookup.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <GeneratingDots count={4} />
        <p className="text-gray-700 mt-4">Loading landing page...</p>
      </div>
    );
  }

  // No page exists for this campaign → offer generation.
  if (!publicId) {
    return (
      <EmptyState
        icon={<Sparkles className="h-12 w-12 text-[#290BD9]" strokeWidth={1} />}
        title="No Landing Page Yet"
        description="Generate a conversion-focused landing page from this campaign's keywords, ad copy, and brand."
        action={
          <Button
            size="lg"
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            className="w-[320px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg"
          >
            {generate.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                STARTING...
              </>
            ) : (
              <>
                <span>GENERATE LANDING PAGE</span>
                <ArrowRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        }
      />
    );
  }

  if (isLoading || !landingPage) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <GeneratingDots count={4} />
        <p className="text-gray-700 mt-4">Loading landing page...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <LandingPageError
        message="This landing page failed to load."
        onRegenerate={() => generate.mutate()}
        regenerating={generate.isPending}
      />
    );
  }

  if (landingPage.status === 'GENERATING') {
    return <LandingPageGenerating />;
  }

  if (landingPage.status === 'ERROR') {
    return (
      <LandingPageError
        message={landingPage.error_detail ?? 'Landing page generation failed.'}
        onRegenerate={() => generate.mutate()}
        regenerating={generate.isPending}
      />
    );
  }

  return (
    <LandingPageView
      landingPage={landingPage}
      organizationId={campaign.organization_id}
      onChanged={() => queryClient.invalidateQueries({ queryKey: ['landing-page', publicId] })}
    />
  );
}
